import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { postEventStream } from "../utils/eventStream";
import { showToast } from "components/ui/toast";
import useDebounce from "hooks/useDebounce";

const errorMessage = (err, fallback) => err?.response?.data?.message || fallback;

const minAddressQueryLength = 5;
const addressTypingPauseMs = 300;

const chatPath = (route, chatId) => route.replace(":chatId", chatId);
const chatKey = (chatId) => [API_ROUTES.PILOT_CHAT, chatId];

export const useGetPilotChatsQuery = () => {
  const { data, isPending } = useQuery({
    queryKey: [API_ROUTES.PILOT_CHATS],
    queryFn: async () => (await api.get(API_ROUTES.PILOT_CHATS)).data.data,
  });
  return { data: data ?? [], isPending };
};

export const useGetPilotChatQuery = (chatId) => {
  const { data, isPending, isError } = useQuery({
    queryKey: chatKey(chatId),
    queryFn: async () => (await api.get(chatPath(API_ROUTES.PILOT_CHAT, chatId))).data.data,
    enabled: !!chatId,
  });
  return { data: data ?? null, isPending, isError };
};

export const useCreatePilotChatMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post(API_ROUTES.PILOT_CHATS)).data.data,
    onSuccess: (chat) => {
      queryClient.setQueryData(chatKey(chat._id), chat);
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.PILOT_CHATS] });
    },
    onError: (err) => showToast("error", errorMessage(err, "Failed to start a chat")),
  });
};

export const useRenamePilotChatMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ chatId, title }) =>
      (await api.put(chatPath(API_ROUTES.PILOT_CHAT, chatId), { title })).data.data,
    onSuccess: ({ _id, title }) => {
      queryClient.setQueryData(chatKey(_id), (chat) => chat && { ...chat, title });
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.PILOT_CHATS] });
    },
    onError: (err) => showToast("error", errorMessage(err, "Failed to rename the chat")),
  });
};

export const useDeletePilotChatMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (chatId) => api.delete(chatPath(API_ROUTES.PILOT_CHAT, chatId)),
    // Quietly, as the agent app deletes a conversation: only a failure says so.
    onSuccess: (_, chatId) => {
      queryClient.removeQueries({ queryKey: chatKey(chatId) });
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.PILOT_CHATS] });
    },
    onError: (err) => showToast("error", errorMessage(err, "Failed to delete thread.")),
  });
};

// The answer to a change card comes back as Pilot's next message, and the card shows how it ended.
export const useConfirmPilotChangeMutation = (chatId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ confirmId, cancel }) =>
      (await api.post(chatPath(API_ROUTES.PILOT_CHAT_CONFIRM, chatId), { confirmId, cancel: cancel || undefined }))
        .data.data,
    onSuccess: ({ status, text }, { confirmId }) =>
      queryClient.setQueryData(chatKey(chatId), (chat) =>
        chat && {
          ...chat,
          messages: [
            ...chat.messages.map((message) => ({
              ...message,
              cards: message.cards?.map((card) => (card.confirmId === confirmId ? { ...card, status } : card)),
            })),
            { _id: `${confirmId}-${status}`, role: "assistant", text, visuals: [], cards: [] },
          ],
        },
      ),
    onError: (err) => showToast("error", errorMessage(err, "Failed to answer the change")),
  });
};

// Each server-sent event of the answer goes to onEvent(event, data) as it arrives.
// `records` are the properties and agents picked from the bar's suggestions: { id, name, kind }; `attachments` the
// files attached for this question: { id, name, type, size }.
export const streamPilotAnswer = ({ chatId, question, records, attachments, signal, onEvent }) =>
  postEventStream(
    chatPath(API_ROUTES.PILOT_CHAT_ASK, chatId),
    { question, records, attachments },
    { onEvent, signal, failureMessage: "Could not answer that. Try again." },
  );

// Files attached in the bar, stored so Pilot can read them: { id, name, type, size } each.
export const attachPilotFiles = async (files) => {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  const response = await api.post(API_ROUTES.PILOT_ATTACHMENTS, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data.data;
};

// Every property on the platform, for the bar's address suggestions.
export const usePilotAddresses = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: [API_ROUTES.PILOT_ADDRESSES],
    queryFn: async () => (await api.get(API_ROUTES.PILOT_ADDRESSES)).data.data,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled,
  });

// Every agent with their network, for the bar's name suggestions.
export const usePilotAgents = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: [API_ROUTES.PILOT_AGENTS],
    queryFn: async () => (await api.get(API_ROUTES.PILOT_AGENTS)).data.data,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled,
  });

const emptyAddressSuggestions = { addresses: [], matchedText: null };

// Addresses the words being typed could be, for one not on the platform yet.
export const usePilotAddressSuggestions = ({ text = "" }, { enabled = true } = {}) => {
  const query = useDebounce(String(text || "").trim(), addressTypingPauseMs);
  const longEnough = query.length >= minAddressQueryLength;

  const { data } = useQuery({
    queryKey: [API_ROUTES.PILOT_ADDRESS_SUGGEST, query],
    queryFn: async () => {
      const response = await api.get(API_ROUTES.PILOT_ADDRESS_SUGGEST, { params: { q: query } });
      if (!response.data?.success) return emptyAddressSuggestions;
      return {
        addresses: response.data.data ?? [],
        matchedText: response.data.matchedText ?? null,
      };
    },
    enabled: enabled && longEnough,
    placeholderData: (previous) => previous,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60,
    retry: false,
  });

  const suggestions = longEnough ? (data ?? emptyAddressSuggestions) : emptyAddressSuggestions;
  const spanStillTyped =
    !!suggestions.matchedText && text.toLowerCase().includes(suggestions.matchedText.toLowerCase());
  return spanStillTyped ? suggestions : emptyAddressSuggestions;
};

// After an answer: the chat as the server saved it, and the list with its new title and order.
export const useRefreshPilotChat = () => {
  const queryClient = useQueryClient();
  return useCallback(
    (chatId) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: chatKey(chatId) }),
        queryClient.invalidateQueries({ queryKey: [API_ROUTES.PILOT_CHATS] }),
      ]),
    [queryClient],
  );
};
